import { useEffect, useRef, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { IDLE_SESSION_TIMEOUT_MS, IDLE_SESSION_WARNING_MS } from "@/constants/sessionPolicy";

interface UseSessionTimeoutProps {
  onTimeout: () => void;
  timeoutDuration?: number; // in milliseconds
  warningDuration?: number; // in milliseconds before timeout to show warning
  isActive?: boolean;
}

/**
 * Hook that manages session timeout functionality
 * Automatically logs out users after a period of inactivity
 */
export const useSessionTimeout = ({
  onTimeout,
  timeoutDuration = IDLE_SESSION_TIMEOUT_MS,
  warningDuration = IDLE_SESSION_WARNING_MS,
  isActive = true,
}: UseSessionTimeoutProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<boolean>(false);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
    warningShownRef.current = false;
  }, []);

  // Show warning before timeout
  const showTimeoutWarning = useCallback(() => {
    if (!warningShownRef.current) {
      warningShownRef.current = true;
      toast({
        title: "Session Expiring Soon",
        description:
          "Your session will expire in 5 minutes due to inactivity. Move your mouse or press any key to stay logged in.",
        variant: "destructive",
        duration: 10000, // Show for 10 seconds
      });
    }
  }, []);

  // Reset the timeout timers
  const resetTimeout = useCallback(() => {
    if (!isActive) return;

    lastActivityRef.current = Date.now();
    clearTimers();
    warningShownRef.current = false;

    // Set warning timer (5 minutes before timeout)
    warningRef.current = setTimeout(() => {
      showTimeoutWarning();
    }, timeoutDuration - warningDuration);

    // Set timeout timer (idle policy)
    timeoutRef.current = setTimeout(() => {
      if (import.meta.env.DEV) {
        console.warn("🕐 Session timeout: Logging out user due to inactivity");
      }
      onTimeout();
    }, timeoutDuration);
  }, [isActive, timeoutDuration, warningDuration, onTimeout, clearTimers, showTimeoutWarning]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    if (!isActive) return;
    resetTimeout();
  }, [isActive, resetTimeout]);

  useEffect(() => {
    if (!isActive) {
      clearTimers();
      return;
    }

    // List of events that constitute user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
    ];

    // Throttle activity detection to avoid excessive timer resets
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const throttledHandleActivity = () => {
      if (throttleTimer) return;

      throttleTimer = setTimeout(() => {
        handleActivity();
        throttleTimer = null;
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, throttledHandleActivity, true);
    });

    // Initialize timeout on mount
    resetTimeout();

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledHandleActivity, true);
      });
      clearTimers();
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [isActive, handleActivity, resetTimeout, clearTimers]);

  // Get remaining time until timeout
  const getRemainingTime = useCallback(() => {
    if (!isActive) return 0;
    const elapsed = Date.now() - lastActivityRef.current;
    return Math.max(0, timeoutDuration - elapsed);
  }, [isActive, timeoutDuration]);

  // Check if session is about to expire
  const isNearExpiry = useCallback(() => {
    const remaining = getRemainingTime();
    return remaining <= warningDuration && remaining > 0;
  }, [getRemainingTime, warningDuration]);

  return {
    resetTimeout,
    getRemainingTime,
    isNearExpiry,
    clearTimers,
  };
};
